import { useEffect, useRef, useState } from 'react';
import { loadLeaflet } from '../../utils/geo';
import { addBaseLayer } from '../../utils/offline/baseLayer';
import { tooltipText } from '../../utils/tooltipText';

/*
 * One pin on a small read-only Leaflet map: where a collection was made.
 * Leaflet rides the lazy `maps` chunk (utils/geo.loadLeaflet), as the
 * national map and MapGpsPicker load it. Nothing to click or drag: a
 * correction is made on the specimen page with a reason, not here.
 */
export default function PinMap({ lat, lng, label = '', height = 220, zoom = 13 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const latitude = Number(lat);
  const longitude = Number(lng);
  const placed = Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!placed) return undefined;
    let alive = true;
    loadLeaflet().then((L) => {
      if (!alive || !containerRef.current) return;
      const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true })
        .setView([latitude, longitude], zoom);
      addBaseLayer(L, map, { attribution: '© OpenStreetMap contributors', maxZoom: 18 });
      const marker = L.marker([latitude, longitude]).addTo(map);
      if (label) marker.bindTooltip(tooltipText(label));
      mapRef.current = map;
    }).catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [placed, latitude, longitude, zoom, label]);

  if (!placed) return null;
  return (
    <div data-testid="pin-map" data-lat={latitude} data-lng={longitude}>
      <div ref={containerRef} role="img" aria-label={label || `${latitude}, ${longitude}`}
        style={{ height, borderRadius: 10, overflow: 'hidden', background: 'var(--c-primary-soft-3)' }} />
      {failed && <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }} data-testid="pin-map-failed">{`${latitude}, ${longitude}`}</p>}
    </div>
  );
}
